import { useMemo } from 'react'
import { useStore } from '~/state/useStore'
import { useDimValues } from '~/api/hooks/useDimValues'
import { makeColorForKey, warnOnCrossFactCatalogDivergence } from './dimValueColors'

/** Catalog-order value-keyed coloring for charts grouped by EXACTLY ONE dim
 *  (spec §4.4): returns a colorForBase for assignGroups, or undefined when the
 *  grouping is not a single dim (multi-dim/composite series keys are not dim
 *  values — positional slots stay correct there) or the catalog has not loaded.
 *  Hooks are called unconditionally (dim='' → empty list, cheap no-op). */
export function useDimValueColorForBase(
  groupBy: string[],
  factTable: string | null,
  kpi: string,
): ((base: string) => string | undefined) | undefined {
  const singleDim = groupBy.length === 1 ? groupBy[0]! : ''
  const catalog = useDimValues(singleDim, factTable ?? '', kpi)
  const palette = useStore((s) => s.personal.chipPalette)
  return useMemo(() => {
    if (!singleDim || !factTable) return undefined
    const values = catalog.data
    if (!values || values.length === 0) return undefined
    warnOnCrossFactCatalogDivergence(singleDim, factTable, values)
    const f = makeColorForKey(values, [], palette)
    return (base: string) => f(base)
  }, [singleDim, factTable, catalog.data, palette])
}
